import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import useLeadsWorkspace from "../components/leadsComponents/hooks/useLeadsWorkspace";
import useSmartView from "../components/leadsComponents/hooks/useSmartView";
import useKeyboardNav from "../components/leadsComponents/hooks/useKeyboardNav";

import CampaignRail from "../components/leadsComponents/workspace/CampaignRail";
import MetricStrip from "../components/leadsComponents/workspace/MetricStrip";
import LeadsLedger from "../components/leadsComponents/workspace/LeadsLedger";
import QueueView from "../components/leadsComponents/workspace/QueueView";
import FollowUpsView from "../components/leadsComponents/workspace/FollowUpsView";
import RepliesView from "../components/leadsComponents/workspace/RepliesView";
import TodayView from "../components/leadsComponents/workspace/TodayView";
import LeadDetailDrawer from "../components/leadsComponents/workspace/LeadDetailDrawer";

import CsvImportWizard from "../components/leadsComponents/import/CsvImportWizard";
import CampaignSettingsModal from "../components/leadsComponents/settings/CampaignSettingsModal";
import TemplateModal from "../components/leadsComponents/TemplateModal";
import TutorialModal from "../components/leadsComponents/tutorial/TutorialModal";

import { SMART_VIEWS } from "../components/leadsComponents/constants";

const DEFAULT_SMART_VIEW = "today";
const TUTORIAL_SEEN_KEY = "bellhopt.leads.tutorialSeen";

export default function LeadsDashboard() {
  const navigate = useNavigate();
  const adminInfo = useSelector((state) => state.login?.adminInfo || {});
  const gmailConnected = Boolean(adminInfo?.gmailConnected || adminInfo?.automationAllowed);

  const workspace = useLeadsWorkspace();
  const {
    campaigns,
    activeCampaign,
    activeCampaignId,
    overview,
    memberRows,
    queueRows,
    activityRows,
    pagination,
    page,
    filters,
    selectedLeadRow,
    selectedLeadDetail,
    loadingCampaigns,
    loadingWorkspace,
    loadingDetail,
    setActiveCampaignId,
    setFilters,
    setPage,
    refreshActiveCampaign,
    createCampaign,
    patchCampaign,
    patchCampaignLead,
    queueAction,
    saveSegment,
    deleteSegment,
    saveLeadNotes,
    openLead,
    closeLead,
  } = workspace;

  const [smartView, setSmartView] = useState(DEFAULT_SMART_VIEW);
  const [activeSegmentKey, setActiveSegmentKey] = useState(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [selectedQueueId, setSelectedQueueId] = useState(null);

  // Auto-open the tour for first-time visitors. The Help button reopens it any
  // time regardless of this flag.
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      if (!window.localStorage?.getItem(TUTORIAL_SEEN_KEY)) {
        setIsTutorialOpen(true);
      }
    } catch (err) {
      // localStorage can throw in private mode; silently ignore.
    }
  }, []);

  const handleCloseTutorial = useCallback(() => {
    setIsTutorialOpen(false);
    try {
      if (typeof window !== "undefined") {
        window.localStorage?.setItem(TUTORIAL_SEEN_KEY, "1");
      }
    } catch (err) {
      // ignore
    }
  }, []);

  const smartViewData = useSmartView({
    smartView,
    memberRows,
    queueRows,
    activityRows,
  });

  // Clear segment highlight when user manually edits filters (so the chip
  // reflects reality).
  useEffect(() => {
    setActiveSegmentKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.stage, filters.status, filters.tags, filters.needsAction]);

  const selectedRowId = selectedLeadRow?.campaignLead?._id || null;

  // For j/k navigation the drawer needs to know the "next" and "previous"
  // rows in the current view.
  const navigableRows = useMemo(() => {
    if (smartView === "all" || smartView === "today") return memberRows;
    if (smartView === "followups") {
      const flat = [];
      for (const key of ["overdue", "today", "tomorrow", "this_week", "later"]) {
        flat.push(...(smartViewData.grouped?.[key] || []));
      }
      return flat;
    }
    return memberRows;
  }, [memberRows, smartView, smartViewData]);

  const navigateRow = useCallback(
    (delta) => {
      if (!navigableRows.length) return;
      const currentIndex = navigableRows.findIndex(
        (row) => row.campaignLead?._id === selectedRowId
      );
      const baseIndex = currentIndex === -1 ? (delta > 0 ? -1 : 0) : currentIndex;
      const nextIndex = Math.min(Math.max(baseIndex + delta, 0), navigableRows.length - 1);
      const target = navigableRows[nextIndex];
      if (target) openLead(target);
    },
    [navigableRows, openLead, selectedRowId]
  );

  // --- Mutations exposed to subcomponents ---

  const handlePatchLead = useCallback(
    async (campaignLeadId, patch) => {
      try {
        await patchCampaignLead(campaignLeadId, patch);
      } catch (error) {
        console.error(error);
      }
    },
    [patchCampaignLead]
  );

  const handleSnooze = useCallback(
    async (target, date) => {
      if (!date) return;
      const id = typeof target === "string" ? target : target?.campaignLead?._id;
      if (!id) return;
      await patchCampaignLead(id, { nextActionAt: date.toISOString() });
    },
    [patchCampaignLead]
  );

  const handleSkipStep = useCallback(
    async (target) => {
      const cl = target?.campaignLead || target;
      if (!cl?._id) return;
      const nextStep = (cl.stepIndex ?? 0) + 1;
      await patchCampaignLead(cl._id, { stepIndex: nextStep });
    },
    [patchCampaignLead]
  );

  const handleBulkStage = useCallback(
    async (ids, stage) => {
      await Promise.all(ids.map((id) => patchCampaignLead(id, { stage }, { silent: true })));
      await refreshActiveCampaign();
    },
    [patchCampaignLead, refreshActiveCampaign]
  );

  const handleBulkEnrollment = useCallback(
    async (ids, enrollmentStatus) => {
      await Promise.all(
        ids.map((id) => patchCampaignLead(id, { enrollmentStatus }, { silent: true }))
      );
      await refreshActiveCampaign();
    },
    [patchCampaignLead, refreshActiveCampaign]
  );

  const handleBulkSnooze = useCallback(
    async (ids, days) => {
      const snoozeDate = new Date();
      snoozeDate.setDate(snoozeDate.getDate() + days);
      snoozeDate.setHours(9, 0, 0, 0);
      await Promise.all(
        ids.map((id) =>
          patchCampaignLead(id, { nextActionAt: snoozeDate.toISOString() }, { silent: true })
        )
      );
      await refreshActiveCampaign();
    },
    [patchCampaignLead, refreshActiveCampaign]
  );

  const handleBulkSkipStep = useCallback(
    async (ids) => {
      const byId = new Map(memberRows.map((row) => [row.campaignLead?._id, row.campaignLead]));
      await Promise.all(
        ids.map((id) => {
          const cl = byId.get(id);
          if (!cl) return Promise.resolve();
          return patchCampaignLead(
            id,
            { stepIndex: (cl.stepIndex ?? 0) + 1 },
            { silent: true }
          );
        })
      );
      await refreshActiveCampaign();
    },
    [memberRows, patchCampaignLead, refreshActiveCampaign]
  );

  const handleBulkSendNow = useCallback(
    async (campaignLeadIds) => {
      const queueIds = queueRows
        .filter((row) => campaignLeadIds.includes(row.campaignLead?._id))
        .map((row) => row.queueItem._id);
      if (queueIds.length) await queueAction("send", queueIds);
    },
    [queueAction, queueRows]
  );

  const handleSendQueueItem = useCallback(
    async (queueItemId) => {
      await queueAction("send", [queueItemId]);
    },
    [queueAction]
  );

  const handleSegmentSelect = useCallback(
    (segment) => {
      const key = segment._id || segment.name;
      setActiveSegmentKey(key);
      setFilters({
        q: segment.filters?.q || "",
        stage: segment.filters?.stage || "",
        status: segment.filters?.status || "",
        tags: segment.filters?.tags || "",
        needsAction: Boolean(segment.filters?.needsAction),
      });
      if (smartView !== "all") setSmartView("all");
    },
    [setFilters, smartView]
  );

  const handleClearSegment = useCallback(() => {
    setActiveSegmentKey(null);
    setFilters({ q: "", stage: "", status: "", tags: "", needsAction: false });
  }, [setFilters]);

  const handleCreateCampaign = useCallback(
    async ({ name, description }) => {
      await createCampaign({ name, description });
    },
    [createCampaign]
  );

  const handleSaveSettings = useCallback(
    async (patch) => {
      if (!activeCampaignId) return;
      await patchCampaign(activeCampaignId, patch);
    },
    [activeCampaignId, patchCampaign]
  );

  const handleImported = useCallback(async () => {
    await refreshActiveCampaign();
  }, [refreshActiveCampaign]);

  // Keyboard navigation. Typing-in-input handling is baked into the hook.
  useKeyboardNav(
    {
      j: () => navigateRow(1),
      k: () => navigateRow(-1),
      arrowdown: () => navigateRow(1),
      arrowup: () => navigateRow(-1),
      escape: () => {
        if (selectedLeadRow) {
          closeLead();
          return true;
        }
        return false;
      },
      "/": () => {
        const input = document.querySelector("[data-search-input]");
        if (input) input.focus();
      },
      "shift+?": () => setIsTutorialOpen(true),
      "?": () => setIsTutorialOpen(true),
      "1": () => setSmartView("today"),
      "2": () => setSmartView("replies"),
      "3": () => setSmartView("followups"),
      "4": () => setSmartView("queue"),
      "5": () => setSmartView("all"),
    },
    { enabled: !isImportOpen && !isSettingsOpen && !isTemplateOpen && !isTutorialOpen }
  );

  const activeSegmentLabel = useMemo(() => {
    if (!activeSegmentKey || !activeCampaign?.savedSegments) return null;
    return (
      activeCampaign.savedSegments.find(
        (segment) => (segment._id || segment.name) === activeSegmentKey
      )?.name || null
    );
  }, [activeCampaign, activeSegmentKey]);

  const centerContent = (() => {
    if (!activeCampaignId) {
      return (
        <div className="flex h-full flex-col items-center justify-center px-6 text-center">
          <p className="text-base font-semibold text-zinc-900">
            Create a campaign to get started
          </p>
          <p className="mt-1 max-w-sm text-sm text-zinc-500">
            Campaigns are the organizing layer for leads, queue review, imports, and reply tracking. Use the left rail to create your first one.
          </p>
        </div>
      );
    }

    if (smartView === "today") {
      return (
        <TodayView
          followUps={smartViewData.followUps || []}
          queue={smartViewData.queue || []}
          replies={smartViewData.replies || []}
          onOpenLead={openLead}
          onSendQueueItem={handleSendQueueItem}
          onSnooze={handleSnooze}
          onSmartViewChange={setSmartView}
          gmailConnected={gmailConnected}
        />
      );
    }

    if (smartView === "queue") {
      return (
        <QueueView
          grouped={smartViewData.grouped || {}}
          total={smartViewData.total || 0}
          selectedQueueId={selectedQueueId}
          onSelectQueue={setSelectedQueueId}
          onQueueAction={queueAction}
          gmailConnected={gmailConnected}
          onOpenLead={openLead}
        />
      );
    }

    if (smartView === "followups") {
      return (
        <FollowUpsView
          grouped={smartViewData.grouped || {}}
          total={smartViewData.total || 0}
          queueRows={queueRows}
          onOpenLead={openLead}
          onSnooze={handleSnooze}
          onSkipStep={handleSkipStep}
          onSendNow={handleSendQueueItem}
          onBulkSnooze={handleBulkSnooze}
          onBulkSkipStep={handleBulkSkipStep}
          onBulkSendNow={handleBulkSendNow}
          gmailConnected={gmailConnected}
          selectedRowId={selectedRowId}
        />
      );
    }

    if (smartView === "replies") {
      return <RepliesView rows={smartViewData.rows || []} onOpenLead={openLead} />;
    }

    // "all"
    return (
      <LeadsLedger
        rows={memberRows}
        loading={loadingWorkspace}
        pagination={pagination}
        page={page}
        filters={filters}
        onFiltersChange={setFilters}
        onPageChange={setPage}
        onOpenLead={openLead}
        onPatchCampaignLead={handlePatchLead}
        onSnooze={handleSnooze}
        onSaveSegment={saveSegment}
        selectedRowId={selectedRowId}
        onBulkStage={handleBulkStage}
        onBulkSnooze={handleBulkSnooze}
        onBulkEnrollment={handleBulkEnrollment}
        activeSegmentLabel={activeSegmentLabel}
        onClearSegment={handleClearSegment}
      />
    );
  })();

  const activeViewMeta = SMART_VIEWS.find((v) => v.key === smartView);

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50 font-body text-zinc-900">
      <CampaignRail
        campaigns={campaigns}
        activeCampaignId={activeCampaignId}
        loadingCampaigns={loadingCampaigns}
        smartView={smartView}
        onSmartViewChange={setSmartView}
        onCampaignChange={setActiveCampaignId}
        onImport={() => setIsImportOpen(true)}
        onTemplates={() => setIsTemplateOpen(true)}
        onNewCampaign={handleCreateCampaign}
        onOpenSettings={() => setIsSettingsOpen(true)}
        onSegmentSelect={handleSegmentSelect}
        onSegmentDelete={deleteSegment}
        overview={overview}
        queueRows={queueRows}
        activityRows={activityRows}
        memberRows={memberRows}
        gmailConnected={gmailConnected}
        onConnectGmail={() => navigate("/settings")}
        activeCampaign={activeCampaign}
        activeSegmentKey={activeSegmentKey}
        onClearSegment={handleClearSegment}
      />

      <main className="flex flex-1 flex-col overflow-hidden bg-white">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 bg-white px-5 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
              <span>{activeCampaign?.name || "No campaign selected"}</span>
              {activeCampaign?.status ? (
                <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-600">
                  {activeCampaign.status}
                </span>
              ) : null}
            </div>
            <h2 className="mt-0.5 text-lg font-semibold tracking-tight text-zinc-900">
              {activeViewMeta?.label || "Workspace"}
              <span className="ml-2 text-sm font-normal text-zinc-500">
                {activeViewMeta?.description || ""}
              </span>
            </h2>
          </div>

          <div className="flex flex-1 items-center justify-end gap-3">
            <MetricStrip
              overview={overview}
              onOpenView={setSmartView}
              smartView={smartView}
            />
            <div className="h-6 w-px bg-zinc-200" />
            <button
              type="button"
              onClick={() => setIsTutorialOpen(true)}
              title="How it works (?)"
              aria-label="Open tutorial"
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-indigo-300 bg-indigo-50 text-[10px] font-bold text-indigo-700">
                ?
              </span>
              How it works
            </button>
            <button
              onClick={refreshActiveCampaign}
              disabled={!activeCampaignId || loadingWorkspace}
              className="rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:text-zinc-400"
            >
              Refresh
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-hidden">
          <div className="h-full">{centerContent}</div>
        </div>
      </main>

      {selectedLeadDetail?.lead ? (
        <LeadDetailDrawer
          detail={selectedLeadDetail}
          loading={loadingDetail}
          activeCampaignId={activeCampaignId}
          activeCampaign={activeCampaign}
          onClose={closeLead}
          onPatchCampaignLead={handlePatchLead}
          onSaveNotes={saveLeadNotes}
          onSnooze={(campaignLeadId, date) => handleSnooze(campaignLeadId, date)}
          onSkipStep={handleSkipStep}
          onEmailSent={async () => {
            await refreshActiveCampaign();
          }}
          onNext={navigableRows.length ? () => navigateRow(1) : null}
          onPrevious={navigableRows.length ? () => navigateRow(-1) : null}
          gmailConnected={gmailConnected}
          onConnectGmail={() => navigate("/settings")}
        />
      ) : null}

      <CsvImportWizard
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        activeCampaignId={activeCampaignId}
        campaignName={activeCampaign?.name}
        lastMapping={adminInfo?.lastLeadImportMapping}
        onImported={handleImported}
      />

      <CampaignSettingsModal
        isOpen={isSettingsOpen}
        campaign={activeCampaign}
        adminInfo={adminInfo}
        onClose={() => setIsSettingsOpen(false)}
        onSave={handleSaveSettings}
      />

      {isTemplateOpen ? (
        <TemplateModal onClose={() => setIsTemplateOpen(false)} />
      ) : null}

      <TutorialModal isOpen={isTutorialOpen} onClose={handleCloseTutorial} />
    </div>
  );
}
